import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import { Seo } from '@/components/Seo';
import { supabase } from '@/lib/supabaseClient';
import { useReveal } from '@/pages/content/v2/shared';
import { ICONS, Icon, usePrefix } from '@/pages/info/v2/InfoShell';
import StoreMap, { type StorePin } from './StoreMap';
import '../../home/v2/home-v2.css';
import '../../content/v2/content-v2.css';
import '../../info/v2/info-v2.css';
import './stores-v2.css';

// New-design Where to Buy (/stores), in the classic page's order: hero,
// search and filters, store cards, the partner banner, the map, then
// authenticity. Stores are the same as
// on the classic page: active rxk_retailers rows plus the partners opted into
// public listing in ERP (core_partners_public_v). The list and the map work
// together (hover a card to lift its pin, click a pin to jump to its card),
// areas come from the data with counts, and "Near me" sorts by distance.
// Phones switch between List and Map.

interface Store {
  id: string;
  name: string;
  type: string;
  area: string;
  address: string | null;
  hours: string | null;
  phone: string | null;
  mapLink: string | null;
  lat: number | null;
  lng: number | null;
}

const TYPE_LABEL: Record<string, string> = { pharmacy: 'Pharmacy', reseller: 'Retail' };
const ALL = '__all';
const clean = (s: string) => s.replace(/\s*[→›]\s*$/, '').replace(/^\s*✓\s*/, '');
const areaName = (s: string | null) => {
  const v = (s || '').trim();
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : 'Others';
};

function km(a: [number, number], b: [number, number]) {
  const r = Math.PI / 180;
  const h = Math.sin(((b[0] - a[0]) * r) / 2) ** 2 + Math.cos(a[0] * r) * Math.cos(b[0] * r) * Math.sin(((b[1] - a[1]) * r) / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(h));
}

export default function StoresV2() {
  const { t } = useTranslation();
  const root = useReveal<HTMLDivElement>();
  const prefix = usePrefix();
  const [type, setType] = useState(ALL);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [area, setArea] = useState(ALL);
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [me, setMe] = useState<[number, number] | null>(null);
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState('');
  const [shown, setShown] = useState(9);

  useEffect(() => {
    Promise.all([
      supabase
        .from('rxk_retailers')
        .select('id, name, type, area, address, hours, phone, map_link, latitude, longitude')
        .eq('status', 'active')
        .order('sort_order', { ascending: true }),
      supabase.from('core_partners_public_v').select('id, name, type, district, contact, address, map_link, hours, latitude, longitude'),
    ]).then(([r, p]) => {
      type Row = Record<string, string | number | null>;
      const manual = ((r.data as Row[]) ?? []).map((x) => ({
        id: String(x.id),
        name: String(x.name),
        type: String(x.type || 'Retail'),
        area: areaName(x.area as string),
        address: (x.address as string) || null,
        hours: (x.hours as string) || null,
        phone: (x.phone as string) || null,
        mapLink: (x.map_link as string) || null,
        lat: (x.latitude as number) ?? null,
        lng: (x.longitude as number) ?? null,
      }));
      const partners = ((p.data as Row[]) ?? []).map((x) => ({
        id: String(x.id),
        name: String(x.name),
        type: TYPE_LABEL[String(x.type)] ?? 'Retail',
        area: areaName(x.district as string),
        address: (x.address as string) || null,
        hours: (x.hours as string) || null,
        phone: (x.contact as string) || null,
        mapLink: (x.map_link as string) || null,
        lat: (x.latitude as number) ?? null,
        lng: (x.longitude as number) ?? null,
      }));
      setStores([...manual, ...partners]);
      setLoading(false);
    });
  }, []);

  const areas = useMemo(() => {
    const m = new Map<string, number>();
    stores.forEach((s) => m.set(s.area, (m.get(s.area) ?? 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [stores]);

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = stores
      .filter((s) => area === ALL || s.area === area)
      .filter((s) => type === ALL || s.type === type)
      .filter((s) => !needle || [s.name, s.area, s.address].some((v) => v?.toLowerCase().includes(needle)))
      .map((s) => ({ ...s, dist: me && s.lat != null && s.lng != null ? km(me, [s.lat, s.lng]) : null }));
    if (me) rows.sort((a, b) => (a.dist ?? 1e9) - (b.dist ?? 1e9));
    return rows;
  }, [stores, area, type, q, me]);

  const pins: StorePin[] = useMemo(
    () => list.filter((s) => s.lat != null && s.lng != null).map((s) => ({ id: s.id, name: s.name, lat: s.lat as number, lng: s.lng as number })),
    [list],
  );

  // Fewer cards at a time keeps the page short; more load on request.
  useEffect(() => setShown(9), [area, type, q, me]);

  const types = useMemo(() => [...new Set(stores.map((s) => s.type))], [stores]);
  // A few real stores float over the hero map.
  const featured = useMemo(() => stores.filter((s) => s.hours && s.lat != null).slice(0, 3), [stores]);

  // "View on map" from a card, or a pin click: select the store; from a card
  // the page scrolls down to the map, from a pin the card is brought into view.
  const pick = (id: string, from: 'card' | 'map') => {
    setSelected(id);
    const at = list.findIndex((s) => s.id === id);
    if (at >= shown) setShown(Math.ceil((at + 1) / 9) * 9);
    window.setTimeout(() => {
      const el = from === 'card' ? document.getElementById('store-map') : document.querySelector(`[data-id="${id}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
  };

  const nearMe = () => {
    if (!navigator.geolocation) return setGeoError(t('storesV2.geoNo', 'Location is not available in this browser.'));
    setLocating(true);
    setGeoError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMe([pos.coords.latitude, pos.coords.longitude]);
        setArea(ALL);
        setLocating(false);
      },
      () => {
        setLocating(false);
        setGeoError(t('storesV2.geoDenied', 'We could not get your location. Please allow location access, or pick an area.'));
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  };

  const directions = (s: Store) =>
    s.mapLink ||
    (s.lat != null && s.lng != null
      ? `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.name + ' ' + (s.address || s.area))}`);

  const nearLabel = locating ? t('storesV2.locating', 'Finding you…') : me ? t('storesV2.nearOn', 'Sorted by distance · Clear') : t('storesV2.near', 'Near me');

  return (
    <>
      <Seo pageKey="whereToBuy" />
      <SharedNavbar />
      <main className="w-full min-h-screen bg-white">
        <div ref={root} className="v2 cnt inf sto" style={{ paddingTop: 'var(--v2h-h, 64px)' }}>
          {/* Hero */}
          <header className="shero">
            <img className="shbg" src="/v2/stores-map.webp" alt="" />
            <div className="wrap">
              <div className="shx">
                <span className="lbl rv">{t('whereToBuy.hero.label')}</span>
                <h1 className="rv" style={{ ['--d' as string]: '.06s' }}>
                  {t('whereToBuy.hero.heading')}
                </h1>
                <p className="rv" style={{ ['--d' as string]: '.12s' }}>
                  {t('whereToBuy.hero.subtitle')}
                </p>
                <div className="shst rv" style={{ ['--d' as string]: '.18s' }}>
                  <span>
                    <b>{loading ? '–' : stores.length}</b> {t('storesV2.partners', 'Partner stores')}
                  </span>
                  <span>
                    <b>{loading ? '–' : areas.length}</b> {t('storesV2.areas', 'Areas')}
                  </span>
                  <span>
                    <Icon d={ICONS.check} />
                    {t('storesV2.genuine', 'Genuine, QR-verified')}
                  </span>
                </div>
                <a href="#stores" className="btn dark rv" style={{ ['--d' as string]: '.24s' }}>
                  {t('storesV2.find', 'Find a store')}
                  <Icon d={ICONS.arrow} />
                </a>
              </div>
              <div className="shf" aria-hidden>
                {featured.map((s, i) => (
                  <div key={s.id} className="shc" style={{ ['--i' as string]: i }}>
                    <span className="ic">
                      <Icon d={ICONS.pin} />
                    </span>
                    <div>
                      <b>{s.name}</b>
                      <small>
                        {s.area} · {s.hours}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </header>

          <div className="wrap sbody">
            {/* Finder: one panel with search, area, type and Near me */}
            <section className="sfil" id="stores">
              <div className="sfind rv">
                <label className="sf-q">
                  <Icon d={ICONS.search} />
                  <span>
                    <small>{t('storesV2.find', 'Find a store')}</small>
                    <input
                      type="search"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder={t('whereToBuy.searchPlaceholder')}
                      aria-label={t('whereToBuy.searchPlaceholder')}
                    />
                  </span>
                  {q && (
                    <button type="button" className="sf-x" onClick={() => setQ('')} aria-label="Clear">
                      <Icon d={ICONS.close} />
                    </button>
                  )}
                </label>
                <label className="sf-a">
                  <Icon d={ICONS.pin} />
                  <span>
                    <small>{t('whereToBuy.filterArea')}</small>
                    <select value={area} onChange={(e) => setArea(e.target.value)} aria-label={t('whereToBuy.filterArea')}>
                      <option value={ALL}>
                        {t('storesV2.allAreas', 'All areas')} ({stores.length})
                      </option>
                      {areas.map(([a, n]) => (
                        <option key={a} value={a}>
                          {a} ({n})
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
                {types.length > 1 && (
                  <div className="sf-t" role="group" aria-label={t('whereToBuy.filterType')}>
                    {[ALL, ...types].map((ty) => (
                      <button key={ty} type="button" className={type === ty ? 'on' : ''} aria-pressed={type === ty} onClick={() => setType(ty)}>
                        {ty === ALL ? t('whereToBuy.filter.all', 'All') : t(`whereToBuy.filter.${ty.toLowerCase()}`, ty)}
                      </button>
                    ))}
                  </div>
                )}
                <button type="button" className={`snear${me ? ' on' : ''}`} onClick={me ? () => setMe(null) : nearMe} disabled={locating}>
                  <Icon d={['M12 2v3M12 19v3M2 12h3M19 12h3', 'M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12z', 'M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z']} />
                  {nearLabel}
                </button>
              </div>
              {geoError && <p className="sgeo">{geoError}</p>}
              <div className="sf-foot">
                <div className="sf-pop">
                  <span>{t('storesV2.popular', 'Popular')}</span>
                  {areas.slice(0, 5).map(([a, n]) => (
                    <button key={a} type="button" className={area === a ? 'on' : ''} onClick={() => setArea(area === a ? ALL : a)}>
                      {a}
                      <em>{n}</em>
                    </button>
                  ))}
                </div>
                {!loading && <p className="scount">{t('whereToBuy.showingResults', { count: list.length, total: stores.length })}</p>}
              </div>
            </section>

            {/* Store cards */}
            <div className="sgrid">
              {loading
                ? [0, 1, 2].map((i) => <div key={i} className="sskel" />)
                : list.slice(0, shown).map((s, i) => (
                    <article
                      key={s.id}
                      data-id={s.id}
                      className={`scard rv${selected === s.id ? ' on' : ''}`}
                      style={{ ['--d' as string]: `${(i % 3) * 0.06}s` }}
                      onMouseEnter={() => setHovered(s.id)}
                      onMouseLeave={() => setHovered(null)}
                    >
                      <div className="sc-h">
                        <span className="sic">
                          <Icon d={ICONS.building} />
                        </span>
                        <div>
                          <h3>{s.name}</h3>
                          <span className="sc-t">{t(`whereToBuy.filter.${s.type.toLowerCase()}`, s.type)}</span>
                        </div>
                        {s.dist != null && <span className="sc-d">{s.dist < 1 ? `${Math.round(s.dist * 1000)} m` : `${s.dist.toFixed(1)} km`}</span>}
                      </div>
                      <ul className="sc-m">
                        {s.address && (
                          <li>
                            <Icon d={ICONS.pin} />
                            {s.address}
                          </li>
                        )}
                        {s.hours && (
                          <li>
                            <Icon d={ICONS.clock} />
                            {s.hours}
                          </li>
                        )}
                      </ul>
                      <div className="sc-b">
                        {s.phone && (
                          <a href={`tel:${s.phone.replace(/[^\d+]/g, '')}`} className="call" aria-label={s.phone}>
                            <Icon d={ICONS.phone} />
                            {s.phone}
                          </a>
                        )}
                        {s.lat != null && (
                          <button type="button" className="see" onClick={() => pick(s.id, 'card')}>
                            {t('storesV2.onMap', 'View on map')}
                          </button>
                        )}
                        <a href={directions(s)} target="_blank" rel="nofollow noopener noreferrer" className="dir" aria-label={t('common.getDirections', 'Get Directions')}>
                          <Icon d={ICONS.arrow} />
                        </a>
                      </div>
                    </article>
                  ))}
            </div>
            {!loading && list.length === 0 && (
              <div className="sempty">
                <p>{t('storesV2.none', 'No stores match that search.')}</p>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => {
                    setQ('');
                    setArea(ALL);
                    setType(ALL);
                  }}
                >
                  {t('storesV2.reset', 'Show all stores')}
                </button>
              </div>
            )}
            {!loading && list.length > shown && (
              <button type="button" className="smore" onClick={() => setShown((n) => n + 9)}>
                {t('storesV2.more', 'Show more stores')}
                <span>{list.length - shown}</span>
              </button>
            )}

            {/* Partner banner */}
            <section className="spart rv">
              <span className="ic">
                <Icon d={ICONS.building} />
              </span>
              <div>
                <h2>{t('whereToBuy.authorizedPartners.heading')}</h2>
                <p>{t('whereToBuy.authorizedPartners.description')}</p>
              </div>
              <Link to={prefix + '/partner-apply'} className="btn">
                {clean(t('whereToBuy.authorizedPartners.button'))}
                <Icon d={ICONS.arrow} />
              </Link>
            </section>

            {/* Map */}
            <section className="smapsec" id="store-map">
              <div className="ihd rv">
                <span className="lbl">{t('whereToBuy.map.title')}</span>
                <h2>{t('whereToBuy.map.subtitle')}</h2>
              </div>
              <div className="smapin rv">
                <StoreMap pins={pins} selected={selected} hovered={hovered} me={me} onSelect={(id) => pick(id, 'map')} />
              </div>
            </section>

            {/* Authenticity */}
            <section className="strust">
              <Link to={prefix + '/verify'} className="rv">
                <span className="ic">
                  <Icon d={ICONS.qr} />
                </span>
                <div>
                  <h3>{clean(t('whereToBuy.authenticityGuaranteed'))}</h3>
                  <p>{t('whereToBuy.qrVerify')}</p>
                  <span className="cmore">
                    {t('common.verify')}
                    <Icon d={ICONS.arrow} />
                  </span>
                </div>
              </Link>
              <Link to={prefix + '/contact'} className="rv" style={{ ['--d' as string]: '.08s' }}>
                <span className="ic">
                  <Icon d={ICONS.shield} />
                </span>
                <div>
                  <h3>{t('whereToBuy.trust.title', '100% Authentic Guarantee')}</h3>
                  <p>{t('whereToBuy.reportCounterfeit')}</p>
                  <span className="cmore">
                    {clean(t('whereToBuy.reportLink'))}
                    <Icon d={ICONS.arrow} />
                  </span>
                </div>
              </Link>
            </section>
          </div>
        </div>
        <SharedFooter />
      </main>
    </>
  );
}
